import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Flex, FlexColumn, Grid, Heading, Icon, Text } from 'core/components';
import { Checkbox, Field, formConsumer } from 'core/form';

import { CLOUD_PROVIDERS } from 'app/util/constants';

import { isGoogleCloud } from '@kentik/ui-shared/util/map';
import { isAws, isAzure } from '@kentik/ui-shared/customDimensions/constants';

import { IoIosCheckmarkCircle, IoIosRadioButtonOff } from 'react-icons/io';
import { awsTile, azureTile, gcpTile, ociTile } from '../constants';

@formConsumer
@inject('$cloudExportWizard', '$clouds', '$auth')
@observer
class ProvidersAndFeatures extends Component {
  state = {
    allowNoMetadata: false
  };

  componentDidMount() {
    const { $auth } = this.props;
    this.setState({ allowNoMetadata: $auth.hasPermission('cloud.metadataless_cloud_exports') });
  }

  renderMetadataCheckbox() {
    const { allowNoMetadata } = this.state;
    const canBeEnabled = allowNoMetadata;

    return (
      <Field name="metadata_collection" showLabel={false} disabled={!canBeEnabled} large>
        <Checkbox />
      </Field>
    );
  }

  renderCloudSpecificOptions() {
    const { $cloudExportWizard } = this.props;
    const { selectedCloudProvider, isFlowLogCollectionEnabled, isAzureVnetLogCollectionEnabled } = $cloudExportWizard;

    if (isAws(selectedCloudProvider)) {
      return (
        <>
          {isFlowLogCollectionEnabled && (
            <>
              <Box ml={1}>
                <Field name="useTerraform" showLabel={false}>
                  <Checkbox />
                </Field>
              </Box>
            </>
          )}
          <Field name="properties.enable_firewall_log_collection" showLabel={false} large>
            <Checkbox />
          </Field>
        </>
      );
    }
    if (isGoogleCloud(selectedCloudProvider)) {
      return (
        <Field name="properties.gcp_cloud_run" showLabel={false} large>
          <Checkbox />
        </Field>
      );
    }

    if (isAzure(selectedCloudProvider)) {
      return (
        <>
          <Field name="properties.enable_azure_vnet_log_collection" showLabel={false} large>
            <Checkbox />
          </Field>
          {isAzureVnetLogCollectionEnabled && (
            <Box ml={1}>
              <Field name="usePowershell" showLabel={false} label="Help me configure my provider via Powershell">
                <Checkbox />
              </Field>
            </Box>
          )}

          <Field name="properties.enable_firewall_log_collection" showLabel={false} large>
            <Checkbox />
          </Field>
        </>
      );
    }

    return null;
  }

  renderObservabilityFeatureOptions() {
    const { $cloudExportWizard } = this.props;
    const { selectedCloudProvider, isSelectedCloudProviderOci } = $cloudExportWizard;

    if (!selectedCloudProvider || selectedCloudProvider === '') {
      return null;
    }

    const csp = `${CLOUD_PROVIDERS[selectedCloudProvider.toUpperCase()].code} Cloud Export`;
    const additionalFeatureCopy = `Please select additional observability features you want to enable for your ${csp}:`;

    return (
      <>
        <Text textAlign="left" fontSize={12} mb={2} muted>
          {additionalFeatureCopy}
        </Text>
        <Box>
          {this.renderMetadataCheckbox()}

          {!isAzure(selectedCloudProvider) && (
            <Field name="properties.enable_flow_log_collection" showLabel={false} large>
              <Checkbox />
            </Field>
          )}

          {this.renderCloudSpecificOptions()}

          {!isSelectedCloudProviderOci && (
            <Field name="properties.enable_metrics_collection" showLabel={false} large>
              <Checkbox />
            </Field>
          )}
        </Box>
      </>
    );
  }

  renderObservabilityFeatures = () => {
    const { allowNoMetadata } = this.state;
    const metadataCopy = 'Metadata collection (required).';
    return (
      <>
        <Heading level={5} py={1} mt={4}>
          Observability Features
        </Heading>
        <Text textAlign="left" fontSize={12} mb={2} muted>
          {allowNoMetadata ? '' : metadataCopy}
        </Text>
        <FlexColumn alignItems="flex-start">{this.renderObservabilityFeatureOptions()}</FlexColumn>
      </>
    );
  };

  handleTileSelection = (tile) => {
    const { $cloudExportWizard } = this.props;
    $cloudExportWizard.resetStore();
    $cloudExportWizard.form.setValue('cloud_provider', tile.provider);
  };

  renderProviderTiles = () => {
    const tiles = [awsTile, gcpTile, azureTile, ociTile];
    const { form } = this.props;
    const selectedCloudProvider = form.getValue('cloud_provider');

    return (
      <Grid gridTemplateColumns="repeat(auto-fit, minmax(110px, 1fr))" gridGap="32px" alignItems="center">
        {tiles.map((tile) => {
          const selectedIcon = tile.provider === selectedCloudProvider ? IoIosCheckmarkCircle : IoIosRadioButtonOff;
          const iconFill = tile.provider === selectedCloudProvider ? '#157ff3' : '';
          return (
            <Flex flexDirection="column" alignItems="center" key={`${tile.provider}-tile`}>
              <Button mr={1} onClick={() => this.handleTileSelection(tile)} borderRadius={15} width="100%">
                <Flex alignItems="center" gap={1} justifyContent="space-between">
                  <Icon icon={tile.logo} iconSize={30} />
                  <Text
                    as="div"
                    flex="1"
                    textAlign="left"
                    textTransform="uppercase"
                    fontWeight="bold"
                    fontSize={15}
                    lineHeight="30px"
                  >
                    {tile.title}
                  </Text>
                  <Icon icon={selectedIcon} iconSize={20} fill={iconFill} />
                </Flex>

                <Text as="div" textAlign="left" fontWeight="300" fontSize={12} mt={2} mb={2} muted>
                  {tile.description}
                </Text>
                <Text as="div" textAlign="left" fontWeight="100" fontSize={12} mt={2} mb={2} muted>
                  {tile.recommendationText}
                </Text>
              </Button>
            </Flex>
          );
        })}
      </Grid>
    );
  };

  render() {
    return (
      <>
        {this.renderProviderTiles()}
        {this.renderObservabilityFeatures()}
      </>
    );
  }
}

export default ProvidersAndFeatures;
